import { AttachmentFile } from "@inrev/common";
import { DateTime } from "luxon";
import {
	ContractExtraction,
	FinancialExtraction,
	UploadedDocumentRow,
	WipExtraction,
} from "../types";

const EMPTY_CONTRACT_EXTRACTIONS: readonly ContractExtraction[] = [];
const EMPTY_WIP_EXTRACTIONS: readonly WipExtraction[] = [];

type PreferableExtraction = {
	extractionCompletedAt?: string;
	createdAt: string;
};

function preferExtraction(
	candidate: PreferableExtraction,
	existing: PreferableExtraction,
): boolean {
	if (
		candidate.extractionCompletedAt !== undefined &&
		existing.extractionCompletedAt !== undefined
	) {
		return (
			DateTime.fromISO(candidate.extractionCompletedAt) >
			DateTime.fromISO(existing.extractionCompletedAt)
		);
	}
	if (candidate.extractionCompletedAt !== undefined) return true;
	if (existing.extractionCompletedAt !== undefined) return false;
	return DateTime.fromISO(candidate.createdAt) > DateTime.fromISO(existing.createdAt);
}

function indexExtractionsByFileId(
	apiExtractions: readonly FinancialExtraction[],
): Map<string, FinancialExtraction> {
	const map = new Map<string, FinancialExtraction>();
	for (const extraction of apiExtractions) {
		const fid = extraction.fileId;
		if (fid !== undefined) {
			const existing = map.get(fid);
			if (!existing || preferExtraction(extraction, existing)) {
				map.set(fid, extraction);
			}
		}
	}
	return map;
}

function indexContractExtractionsByFileId(
	contractExtractions: readonly ContractExtraction[],
): Map<string, ContractExtraction> {
	const map = new Map<string, ContractExtraction>();
	for (const extraction of contractExtractions) {
		const existing = map.get(extraction.fileId);
		if (!existing || (existing.pdfFileId == null && extraction.pdfFileId != null)) {
			map.set(extraction.fileId, extraction);
		}
	}
	return map;
}

function indexWipExtractionsByFileId(
	wipExtractions: readonly WipExtraction[],
): Map<string, WipExtraction> {
	const map = new Map<string, WipExtraction>();
	for (const extraction of wipExtractions) {
		const existing = map.get(extraction.fileId);
		if (!existing || preferExtraction(extraction, existing)) {
			map.set(extraction.fileId, extraction);
		}
	}
	return map;
}

export function mapAttachmentsToUploadedDocumentsRow(
	attachments: AttachmentFile[],
	apiExtractions: readonly FinancialExtraction[],
	contractExtractions: readonly ContractExtraction[] = EMPTY_CONTRACT_EXTRACTIONS,
	wipExtractions: readonly WipExtraction[] = EMPTY_WIP_EXTRACTIONS,
): UploadedDocumentRow[] {
	const byFileId = indexExtractionsByFileId(apiExtractions);
	const contractByFileId = indexContractExtractionsByFileId(contractExtractions);
	const wipByFileId = indexWipExtractionsByFileId(wipExtractions);
	return attachments.map((a) => ({
		...a,
		financialExtraction: byFileId.get(a.file.id),
		contractExtraction: contractByFileId.get(a.file.id),
		wipExtraction: wipByFileId.get(a.file.id),
	}));
}
